import React, { useEffect, useState } from 'react';
import { TextField, Button, Typography, Box, Stack, IconButton, InputAdornment } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Notification from '../../../components/Notification';
import axios from 'axios';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { customLog, showSuccessToast } from '../../../utils/utils';
import { customAxios } from '../../../lib/axios';
import { VIEW } from '../../../utils/breakpoint';

interface LoginFormState {
    username: string;
    password: string;
}

interface LoginFormProps {
    isMobile: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ isMobile }) => {
    const navigate = useNavigate();
    const { login, isLoggedIn, profile, setProfile } = useAuth();
    const [form, setForm] = useState<LoginFormState>({
        username: '',
        password: '',
    });

    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const queryProfile = async (accessToken: string) => {
        customLog("querying profile");
        return await customAxios({
            method: "GET",
            url: `${process.env.REACT_APP_SERVER_URL}/api/v1/rms/auth/profile/`,
            headers: {
                "Authorization": `Bearer ${accessToken}`,
            },
        }).then(async (response) => {
            customLog("checkingggg", response.data);
            await localStorage.setItem('profile', JSON.stringify(response.data));
            setProfile(response.data)
            return response
        }
        ).catch((error) => {
            console.error(error);
        });
    };

    useEffect(() => {
        if (!isLoggedIn || !profile) {
            return
        }
        setSnackbarMessage('Checking successful. Redirecting to dashboard...');
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        setTimeout(() => {
            navigate('/');
        }, 500);

    }, [isLoggedIn, profile])

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        customLog("form", form);
        customLog(`${process.env.REACT_APP_SERVER_URL}/api/v1/rms/auth/login/`)
        event.preventDefault();
        await axios({
            method: "POST",
            url: `${process.env.REACT_APP_SERVER_URL}/api/v1/rms/auth/login/`,
            headers: {
                "Content-Type": "application/json",
            },
            data: form,
        })
            .then(async (response) => {
                customLog(response.data);
                if (response.status === 200) {
                    customLog("response", response.data);
                    login(response.data.access, response.data.refresh);
                    await queryProfile(response.data.access);
                    // setSnackbarMessage('Checking successful. Redirecting to dashboard...');
                    // setSnackbarSeverity("success");
                    // setOpenSnackbar(true);
                    // setTimeout(() => {
                    //     navigate('/');
                    // }, 2000);
                }
            })
            .catch(function (error) {
                if (503 === error.response.status) {
                    setSnackbarMessage(error.response.data.detail || 'Service unavailable. Please try again later.');
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                    return;
                }
                if (error.response.status === 401) {
                    customLog("error", error.response.data.error);
                    setSnackbarMessage('Invalid credentials. Please try again.');
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                }
                else {
                    setSnackbarMessage('Something went wrong. Please try again.');
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                }
            });
    }



    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignContent: 'center', margin: "auto 10px", }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', margin: "auto 10px", }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Welcome Back
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                    Sign in to continue RoboHub
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
                    <TextField
                        name="username"
                        // label="Username"
                        variant="outlined"
                        margin="normal"
                        placeholder="Username"
                        type="email"
                        fullWidth
                        value={form.username}
                        onChange={handleFieldChange}
                        InputLabelProps={{
                            shrink: true  // This ensures the label always stays floated
                        }}
                    />
                    <TextField
                        name="password"
                        // label="Password"
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={form.password}
                        onChange={handleFieldChange}
                        InputLabelProps={{
                            shrink: true,  // This ensures the label always stays floated
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                            <Typography variant="body2" sx={{ color: '#A0A0A0', fontSize: '12px' }}>
                                Forgot password?
                            </Typography>
                        </Link>
                    </Box>
                    <Button type="submit" variant="contained" sx={{ mt: 2, width: '100%', backgroundColor: '#12B76A', mb: 4 }}>
                        Login
                    </Button>
                </Box>

                <Box sx={(theme) => ({
                    [theme.breakpoints.down(VIEW.TABLET)]: {
                        mb: 12
                    }
                })}>
                    <Typography>
                        By logging in, you agree to our{' '}
                        <Link to="/terms-of-use" style={{ textDecoration: 'none' }}>
                            <span style={{ color: '#12B76A', display: 'inline' }}>
                                Terms of Use
                            </span>
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy-policy" style={{ textDecoration: 'none' }}>
                            <span style={{ color: '#12B76A', display: 'inline' }}>
                                Privacy Policy
                            </span>
                        </Link>.
                    </Typography>
                </Box>
            </Box>
            <Notification
                openSnackbar={openSnackbar}
                handleCloseSnackbar={handleCloseSnackbar}
                snackbarMessage={snackbarMessage}
                snackbarSeverity={snackbarSeverity}
                vertical="bottom"
                horizontal="right"
            />
        </Box>
    );
};

export default LoginForm;
